import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { mapApiError } from "../../../../shared/utils/mapApiError";
import { getContenidoScope } from "../api/contenidoApi";

function formatScopeNames(items, ids, allLabel = "Todas") {
  if (Array.isArray(items) && items.length) {
    return items.map((item) => item.name).join(", ");
  }

  if (Array.isArray(ids) && ids.length) {
    return `IDs: ${ids.join(", ")}`;
  }

  return allLabel;
}

function formatDateTime(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getWeekdayLabel(value) {
  const labels = {
    1: "Lunes",
    2: "Martes",
    3: "Miércoles",
    4: "Jueves",
    5: "Viernes",
    6: "Sábado",
    7: "Domingo",
  };

  return labels[Number(value)] || "-";
}

function getReadRequirementLabel(scope) {
  const requirement = scope?.readRequirement;

  if (requirement === "ONCE") {
    return "Una sola vez";
  }

  if (requirement === "WEEKLY") {
    return `Cada ${scope.readRepeatEvery || 1} semana(s), los ${getWeekdayLabel(
      scope.readWeekday
    )}`;
  }

  if (requirement === "MONTHLY") {
    return `Cada ${scope.readRepeatEvery || 1} mes(es), el día ${
      scope.readMonthDay || "-"
    }`;
  }

  return "No obligatoria";
}

function SummaryCard({ label, value, color = "primary.900" }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 145,
        p: 1.4,
        borderRadius: "16px",
        bgcolor: "neutral.100",
        border: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "neutral.600" }}>
        {label}
      </Typography>

      <Typography sx={{ mt: 0.4, fontSize: 18, fontWeight: 900, color }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function ContenidoScopeModal({ open, contentId, title, onClose }) {
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !contentId) {
      setScope(null);
      setError("");
      setLoading(false);
      return;
    }

    let alive = true;

    (async () => {
      setLoading(true);

      try {
        const nextScope = await getContenidoScope(contentId);
        if (!alive) return;

        setScope(nextScope);
        setError("");
      } catch (err) {
        if (!alive) return;

        setScope(null);
        setError(mapApiError(err).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, contentId]);

  const viewers = useMemo(() => {
    const list = Array.isArray(scope?.viewers) ? scope.viewers : [];

    return [...list].sort((a, b) => {
      if (a.viewedCurrentPeriod !== b.viewedCurrentPeriod) {
        return a.viewedCurrentPeriod ? 1 : -1;
      }

      return String(a.fullName || "").localeCompare(String(b.fullName || ""));
    });
  }, [scope]);

  const summary = scope?.viewsSummary;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {title ? `Alcance de: ${title}` : "Alcance del contenido"}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
            Cargando alcance...
          </Typography>
        ) : error ? (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "error.main" }}>
            {error}
          </Typography>
        ) : scope ? (
          <Stack spacing={2.2}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  p: 1.8,
                  borderRadius: "18px",
                  bgcolor: "neutral.50",
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 900,
                    color: "primary.900",
                    mb: 1.1,
                  }}
                >
                  Alcance
                </Typography>
                
                <Stack spacing={0.8}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                    Sucursales:{" "}
                    {scope.isAllBranches
                      ? "Todas"
                      : formatScopeNames(scope.branches, scope.branchIds, "Todas")}
                  </Typography>
                    
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                    Áreas:{" "}
                    {scope.isAllAreas
                      ? "Todas"
                      : formatScopeNames(scope.areas, scope.areaIds, "Todas")}
                  </Typography>
                    
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                    Roles:{" "}
                    {scope.isAllRoles
                      ? "Todos"
                      : formatScopeNames(scope.roles, scope.roleIds, "Todos")}
                  </Typography>
                </Stack>
              </Box>
                    
              <Box
                sx={{
                  p: 1.8,
                  borderRadius: "18px",
                  bgcolor: "rgba(175,186,217,0.22)",
                  border: "1px solid rgba(24,34,93,0.10)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 900,
                    color: "primary.900",
                    mb: 1.1,
                  }}
                >
                  Frecuencia de lectura
                </Typography>
                
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                      label={getReadRequirementLabel(scope)}
                      sx={{
                        fontWeight: 900,
                        borderRadius: "10px",
                        bgcolor:
                          scope.readRequirement === "NONE"
                            ? "neutral.200"
                            : "secondary.200",
                        color: "primary.900",
                      }}
                    />
            
                    {scope.readUntil ? (
                      <Chip
                        label={`Hasta: ${formatDate(scope.readUntil)}`}
                        sx={{
                          fontWeight: 900,
                          borderRadius: "10px",
                          bgcolor: "neutral.100",
                          color: "primary.900",
                        }}
                      />
                    ) : null}
                  </Stack>
                  
                  {scope.currentPeriodKey ? (
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "neutral.700" }}>
                      Período actual: {formatDate(scope.currentPeriodStart)} -{" "}
                      {formatDate(scope.currentPeriodEnd)}
                    </Typography>
                  ) : scope.readRequirement !== "NONE" ? (
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "neutral.700" }}>
                      No hay un período activo en este momento.
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "neutral.700" }}>
                      Este contenido no exige lectura periódica.
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>
                
            <Divider />

            <Stack spacing={1.4}>
              <Typography sx={{ fontSize: 15, fontWeight: 900, color: "primary.900" }}>
                Visualizaciones
              </Typography>

              <Stack direction="row" spacing={1.2} sx={{ flexWrap: "wrap" }}>
                <SummaryCard
                  label="Empleados alcanzados"
                  value={summary?.totalTargetEmployees ?? 0}
                />
                <SummaryCard
                  label="Vieron este período"
                  value={summary?.viewedCurrentPeriod ?? 0}
                  color="success.main"
                />
                <SummaryCard
                  label="Pendientes"
                  value={summary?.pendingCurrentPeriod ?? 0}
                  color="error.main"
                />
                <SummaryCard
                  label="Visualizaciones totales"
                  value={summary?.totalViews ?? 0}
                />
              </Stack>

              <Box
                sx={{
                  borderRadius: "18px",
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.06)",
                  bgcolor: "neutral.50",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.4fr 0.8fr 1fr 0.6fr",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: "neutral.300",
                  }}
                >
                  {["Empleado", "Estado", "Última vista", "Total"].map((head) => (
                    <Typography
                      key={head}
                      sx={{ fontSize: 12, fontWeight: 900, color: "primary.900" }}
                    >
                      {head}
                    </Typography>
                  ))}
                </Box>

                {viewers.length ? (
                  viewers.map((viewer) => (
                    <Box
                      key={viewer.employeeId}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.4fr 0.8fr 1fr 0.6fr",
                        gap: 1,
                        alignItems: "center",
                        px: 1.5,
                        py: 1,
                        borderBottom: "1px solid rgba(0,0,0,0.04)",
                      }}
                    >
                      <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "primary.900" }}>
                        {viewer.fullName || `Empleado ${viewer.employeeId}`}
                      </Typography>

                      <Chip
                        label={viewer.viewedCurrentPeriod ? "Visto" : "Pendiente"}
                        size="small"
                        sx={{
                          justifySelf: "flex-start",
                          fontWeight: 900,
                          borderRadius: "10px",
                          bgcolor: viewer.viewedCurrentPeriod ? "#b9f6ca" : "#ffcdd2",
                          color: "primary.900",
                        }}
                      />

                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "neutral.700" }}>
                        {formatDateTime(viewer.lastViewedAt)}
                      </Typography>

                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "primary.900" }}>
                        {viewer.totalViews}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ px: 1.5, py: 2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "neutral.700" }}>
                      No hay empleados alcanzados para este contenido.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Stack>
        ) : (
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "neutral.700" }}>
            Sin datos de alcance.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}